import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import LocalShipping from '@mui/icons-material/LocalShipping';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Inventory from '@mui/icons-material/Inventory';
import Place from '@mui/icons-material/Place';
import Person from '@mui/icons-material/Person';
import Phone from '@mui/icons-material/Phone';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Map from '@mui/icons-material/Map';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAuth } from '../context/AuthContext';
import { useOrders } from '../context/OrderContext';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Type assertion for the icon fix
const DefaultIcon = L.icon({
    iconUrl: icon as unknown as string,
    shadowUrl: iconShadow as unknown as string,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const steps = ['Order Placed', 'Confirmed', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered'];

const getStepIndex = (status: string) => {
  switch (status.toLowerCase()) {
    case 'pending': return 0;
    case 'confirmed': return 1;
    case 'processing': return 2;
    case 'shipped': return 3;
    case 'out_for_delivery': return 4;
    case 'delivered': return 5;
    default: return 0;
  }
};

const OrderTracking: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getOrderById } = useOrders();
  
  const order = orderId ? getOrderById(orderId) : null;
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (order) {
      setActiveStep(getStepIndex(order.status));
    }
  }, [order]);

  if (!orderId) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">No Order ID provided</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/account')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading order details...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Button 
        startIcon={<ArrowBack />} 
        onClick={() => navigate('/account')} 
        sx={{ mb: 2 }}
      >
        Back to Orders
      </Button>

      <Grid container spacing={3}>
        {/* Order Status Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Order #{order.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Placed on {new Date(order.date).toLocaleDateString()}
                </Typography>
              </Box>
              <Chip 
                label={order.status.replace('_', ' ').toUpperCase()} 
                color={order.status === 'delivered' ? 'success' : 'primary'} 
                sx={{ fontWeight: 'bold' }}
              />
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {order.status !== 'delivered' && (
              <Box sx={{ mt: 4, p: 2, bgcolor: 'primary.50', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                <LocalShipping color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Estimated Delivery
                  </Typography>
                  <Typography variant="body2">
                    {new Date(new Date(order.date).setDate(new Date(order.date).getDate() + 3)).toDateString()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Order Details & Map */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Tracking Updates
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <List>
              {/* Mock tracking history */}
              {activeStep >= 3 && (
                <ListItem>
                  <ListItemIcon><LocalShipping color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Package has left the facility" 
                    secondary={`${new Date(order.date).toLocaleDateString()} - 10:00 AM`} 
                  />
                </ListItem>
              )}
              {activeStep >= 2 && (
                <ListItem>
                  <ListItemIcon><Inventory /></ListItemIcon>
                  <ListItemText 
                    primary="Order processed and packed" 
                    secondary={`${new Date(order.date).toLocaleDateString()} - 09:30 AM`} 
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Order confirmed" 
                  secondary={`${new Date(order.date).toLocaleDateString()} - 08:00 AM`} 
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 3 }}>
               <Typography variant="h6" gutterBottom>
                 Live Location
               </Typography>
               <Box 
                 sx={{ 
                   height: 400, 
                   width: '100%',
                   borderRadius: 1,
                   overflow: 'hidden',
                   '& .leaflet-container': {
                     height: '100%',
                     width: '100%',
                     zIndex: 0
                   }
                 }}
               >
                 <MapContainer center={[-1.2921, 36.8219]} zoom={13} scrollWheelZoom={false}>
                   <TileLayer
                     attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                     url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                   />
                   <Marker position={[-1.2921, 36.8219]}>
                     <Popup>
                       Order #{order.id} <br /> Current Location
                     </Popup>
                   </Marker>
                 </MapContainer>
               </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Delivery Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Delivery Address
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Person sx={{ color: 'text.secondary', mr: 2 }} />
              <Typography>{user?.name || 'Customer Name'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Place sx={{ color: 'text.secondary', mr: 2 }} />
              <Typography>
                123 Green Lane, Nairobi, Kenya
                {/* Should come from order details in real app */}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Phone sx={{ color: 'text.secondary', mr: 2 }} />
              <Typography>+254 712 345 678</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Items
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List disablePadding>
              {order.items.map((item: any, index: number) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText 
                    primary={item.name} 
                    secondary={`Qty: ${item.quantity} x KSh ${item.price}`} 
                  />
                  <Typography fontWeight="bold">
                    KSh {(item.price * item.quantity).toLocaleString()}
                  </Typography>
                </ListItem>
              ))}
              <Divider sx={{ my: 1 }} />
              <ListItem sx={{ px: 0 }}>
                <ListItemText primary="Total" primaryTypographyProps={{ fontWeight: 'bold' }} />
                <Typography fontWeight="bold" color="primary.main">
                  KSh {order.total.toLocaleString()}
                </Typography>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrderTracking;